/* ==========================================================================
   Provider Assignments tab (PORTAL-204)
   Each provider sees prices from one pricing list, or the default price.
   Open to every pharmacy user, like provider catalog assignment.
   ========================================================================== */

import { useMemo, useState } from 'react'
import { AlertTriangle, Search } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Checkbox } from '@/components/ui/Checkbox'
import { useToast } from '@/context/ToastContext'
import { assignPricingList } from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type { PricingList, ProviderResponse } from '@/api/types'
import { DEFAULT_PRICE_LABEL, DEFAULT_PRICE_OPTION } from './PricingListModals'

interface PricingProvidersTabProps {
  slug: string
  providers: ProviderResponse[]
  lists: PricingList[]
  onAssigned: () => void
}

function providerLabel(provider: ProviderResponse): string {
  return provider.name || provider.email
}

export function PricingProvidersTab({ slug, providers, lists, onAssigned }: PricingProvidersTabProps) {
  const { success: showSuccess, error: showError } = useToast()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkTarget, setBulkTarget] = useState<string>(lists[0]?.id ?? DEFAULT_PRICE_OPTION)
  const [saving, setSaving] = useState(false)

  const options = [
    { value: DEFAULT_PRICE_OPTION, label: DEFAULT_PRICE_LABEL },
    ...lists.map((l) => ({ value: l.id, label: l.name })),
  ]
  const optionLabel = (value: string) =>
    options.find((o) => o.value === value)?.label ?? DEFAULT_PRICE_LABEL

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return providers
    return providers.filter((p) =>
      [p.name, p.email, p.primary_clinic_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    )
  }, [providers, search])

  const unassigned = providers.filter((p) => !p.pricing_list_id).length
  const allShownChecked = shown.length > 0 && shown.every((p) => selected.has(p.id))

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAllShown() {
    setSelected((prev) => {
      const next = new Set(prev)
      shown.forEach((p) => (allShownChecked ? next.delete(p.id) : next.add(p.id)))
      return next
    })
  }

  async function assign(providerIds: string[], value: string, message: string) {
    setSaving(true)
    try {
      await assignPricingList(slug, providerIds, value === DEFAULT_PRICE_OPTION ? null : value)
      showSuccess(message)
      onAssigned()
      return true
    } catch (err) {
      showError(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to assign pricing list. Please try again.'
      )
      return false
    } finally {
      setSaving(false)
    }
  }

  async function handleBulkAssign() {
    const ids = [...selected]
    const ok = await assign(
      ids,
      bulkTarget,
      `${ids.length} ${ids.length === 1 ? 'provider' : 'providers'} → ${optionLabel(bulkTarget)}`
    )
    if (ok) setSelected(new Set())
  }

  return (
    <div className="space-y-4">
      {providers.length > 0 && unassigned > 0 && (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {unassigned} of {providers.length}{' '}
            {unassigned === 1 ? 'provider has' : 'providers have'} no pricing list, so they
            see each product’s default price. That’s fine. It’s shown here so it’s never a
            surprise.
          </span>
        </div>
      )}

      <Card className="overflow-visible">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-charcoal">Providers</h2>
            <p className="text-sm text-gray-500">
              Each provider sees prices from one pricing list, or the default price.
            </p>
          </div>
          <div className="relative sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              aria-label="Search providers or clinics"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search providers or clinics…"
              className="pl-9"
            />
          </div>
        </div>

        {selected.size > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 rounded-md bg-soft-lavender/40 p-3">
            <span className="text-sm font-medium text-charcoal">
              {selected.size} {selected.size === 1 ? 'provider' : 'providers'} selected
            </span>
            <div className="sm:w-64">
              <Select options={options} value={bulkTarget} onChange={setBulkTarget} disabled={saving} />
            </div>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={handleBulkAssign} loading={saving}>
                Assign
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} disabled={saving}>
                Clear
              </Button>
            </div>
          </div>
        )}

        {providers.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            No providers yet. Invite providers from the Providers page.
          </p>
        ) : shown.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            No providers match “{search}”.
          </p>
        ) : (
          <>
            {/* Desktop table */}
            <table className="hidden sm:table w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="w-10 pb-3 text-left">
                    <Checkbox
                      aria-label="Select all shown providers"
                      checked={allShownChecked}
                      onChange={toggleAllShown}
                    />
                  </th>
                  <th className="text-left text-sm font-medium text-gray-600 pb-3">Provider</th>
                  <th className="text-left text-sm font-medium text-gray-600 pb-3">Clinic</th>
                  <th className="text-left text-sm font-medium text-gray-600 pb-3 w-72">Pricing list</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((provider) => (
                  <tr key={provider.id} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-3">
                      <Checkbox
                        aria-label={`Select ${providerLabel(provider)}`}
                        checked={selected.has(provider.id)}
                        onChange={() => toggle(provider.id)}
                      />
                    </td>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-charcoal">{providerLabel(provider)}</div>
                      {provider.name && <div className="text-xs text-gray-500">{provider.email}</div>}
                    </td>
                    <td className="py-3 pr-4 text-sm text-gray-600">
                      {provider.primary_clinic_name || '—'}
                    </td>
                    <td className="py-3">
                      <Select
                        options={options}
                        value={provider.pricing_list_id ?? DEFAULT_PRICE_OPTION}
                        onChange={(value) =>
                          assign([provider.id], value, `${providerLabel(provider)} → ${optionLabel(value)}`)
                        }
                        disabled={saving}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {shown.map((provider) => (
                <div key={provider.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      aria-label={`Select ${providerLabel(provider)}`}
                      checked={selected.has(provider.id)}
                      onChange={() => toggle(provider.id)}
                    />
                    <div>
                      <div className="font-medium text-charcoal">{providerLabel(provider)}</div>
                      {provider.name && <div className="text-xs text-gray-500">{provider.email}</div>}
                      {provider.primary_clinic_name && (
                        <div className="text-xs text-gray-500">{provider.primary_clinic_name}</div>
                      )}
                    </div>
                  </div>
                  <Select
                    options={options}
                    value={provider.pricing_list_id ?? DEFAULT_PRICE_OPTION}
                    onChange={(value) =>
                      assign([provider.id], value, `${providerLabel(provider)} → ${optionLabel(value)}`)
                    }
                    disabled={saving}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
